import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  IGetRateForm,
  IGetRateItem,
  IQuoteSummary,
  IGetRateResponse,
  TRateErrors,
} from '@features/Rate/types/GetRateTypes';
import { initialState, initialItem } from '@features/Rate/helpers/constants';
import { IItemInput } from '@/features/Trailer3D/types/Trailer3D.types';
import * as Helpers from '@features/Rate/helpers/calculations';
import { IZipCodeOption } from '@/@core/types/Zones.types';

export interface GetRateState {
  originZipCode: IZipCodeOption | null;
  destinationZipCode: IZipCodeOption | null;
  quoteSummary: IQuoteSummary;
  error: string | null;
  errors: TRateErrors;
  form: IGetRateForm;
  loading: boolean;
  currentStep: number;
  rateResponse: IGetRateResponse | null;
  trailer3DItems: IItemInput[];
}

export const rateSlice = createSlice({
  name: 'Rate',
  initialState,
  reducers: {
    updateFormField: (state, action: PayloadAction<Partial<IGetRateForm>>) => {
      state.form = { ...state.form, ...action.payload };
      state.quoteSummary = Helpers.calculateQuoteSummary({ ...state.form, ...action.payload });
    },
    updateItem: (state, action: PayloadAction<{ id: string; updates: Partial<IGetRateItem> }>) => {
      const itemIndex = state.form.items.findIndex((item) => item.id === action.payload.id);
      if (itemIndex !== -1) {
        const updatedItem = { ...state.form.items[itemIndex], ...action.payload.updates };

        updatedItem.cubicPerPiece = Helpers.calculateCubicPerPiece(updatedItem);
        updatedItem.totalCubic = Helpers.calculateTotalCubic(updatedItem);
        updatedItem.density = Helpers.calculateDensity(updatedItem);

        const isFreightClassExplicitlySet = 'freightClass' in action.payload.updates;
        const isFreightClassCleared =
          isFreightClassExplicitlySet &&
          (action.payload.updates.freightClass === undefined ||
            action.payload.updates.freightClass === null ||
            action.payload.updates.freightClass === 0);

        if (updatedItem.density > 0) {
          if (!isFreightClassExplicitlySet || isFreightClassCleared) {
            updatedItem.freightClass = Helpers.calculateFreightClass(updatedItem.density);
          }
        } else {
          if (!isFreightClassExplicitlySet) {
            updatedItem.freightClass = undefined;
          }
        }

        state.form.items[itemIndex] = updatedItem;
        state.quoteSummary = Helpers.calculateQuoteSummary(state.form);
      }
    },
    addItem: (state) => {
      state.form.items.push(initialItem());
    },
    removeItem: (state, action: PayloadAction<string>) => {
      state.form.items = state.form.items.filter((item) => item.id !== action.payload);
      if (state.form.items.length === 0) {
        state.form.items = [initialItem()];
      }
      state.quoteSummary = Helpers.calculateQuoteSummary(state.form);
    },
    toggleAdditionalService: (state, action: PayloadAction<string>) => {
      const serviceIndex = state.form.additionalServices.indexOf(action.payload);
      if (serviceIndex === -1) {
        state.form.additionalServices.push(action.payload);
      } else {
        state.form.additionalServices.splice(serviceIndex, 1);
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetForm: (state) => {
      state.form = initialState.form;
      state.quoteSummary = initialState.quoteSummary;
      state.error = null;
      state.currentStep = 1;
      state.rateResponse = null;
      state.originZipCode = null;
      state.destinationZipCode = null;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setRateResponse: (state, action: PayloadAction<IGetRateResponse | null>) => {
      state.rateResponse = action.payload;
    },
    updateEstimatedRate: (state, action: PayloadAction<number | undefined>) => {
      state.quoteSummary.estimatedRate = action.payload;
    },
    setErrors: (state, action: PayloadAction<TRateErrors>) => {
      state.errors = action.payload;
    },
    removeErrors: (state, action: PayloadAction<Partial<TRateErrors>>) => {
      state.errors = { ...state.errors, ...action.payload };
    },
    resetErrors: (state) => {
      state.errors = {};
    },
    setOriginZipCode: (state, action: PayloadAction<IZipCodeOption | null>) => {
      state.originZipCode = action.payload;
    },
    setDestinationZipCode: (state, action: PayloadAction<IZipCodeOption | null>) => {
      state.destinationZipCode = action.payload;
    },
    setTrailer3DItems: (state, action: PayloadAction<IItemInput[]>) => {
      state.trailer3DItems = action.payload;
    },
  },
});

export const rateActions = rateSlice.actions;
